import { TableV2 as Table } from "@asayinc/component-library";
import { FilterList } from "@mui/icons-material";
import { Stack } from "@mui/material";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { DateFilter } from "src/components/molecules/DateFilter";
import { EmptyState } from "src/components/molecules/EmptyState";
import { TableMultiSelectFilter } from "src/components/molecules/TableMultiSelectFilter";
import {
  ROW_OPTIONS,
  NOTIFICATION_STATUS_OPTIONS,
  CORPORATE_ACTION_NOTIFICATION_OPTIONS,
} from "src/constants/options";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { useDateQueryParams } from "src/hooks/useDateQueryParams";
import { useSearchFns } from "src/hooks/useSearchFns";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useTableSearchParams } from "src/hooks/useTableSearchParams";
import { useCustomerCorporateActionNotificationsQuery } from "src/store/customer";
import { ListNotification } from "src/store/types";
import { getColumnData, getColumns, getRowData } from "./utils";

interface ICustomerCorporateActionNotificationsTable {
  customerId: string;
  onRowClick: (communication: ListNotification) => void;
  selectedNotification?: ListNotification | null;
}

export function CustomerCorporateActionNotificationsTable({
  customerId,
  onRowClick,
  selectedNotification,
}: ICustomerCorporateActionNotificationsTable) {
  const methods = useForm();
  const { search, status, type } = useTableSearchParams();
  const { at, before, after } = useDateQueryParams(TABLE_SEARCH_PARAMS.SENT_AT);

  const {
    getSort,
    ordering,
    offset,
    limit,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const searchFns = useSearchFns();
  const { data, isFetching, isLoading } =
    useCustomerCorporateActionNotificationsQuery({
      id: customerId,
      limit,
      ordering,
      offset,
      type,
      status,
      search,
      sentAt: at,
      sentAfter: after,
      sentBefore: before,
    });
  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const columns = useMemo(() => getColumns(), []);
  const columnData = useMemo(
    () => getColumnData({ getSort, onOrderingChange }),
    [getSort, onOrderingChange]
  );
  const rowData = getRowData(
    data?.results || [],
    onRowClick,
    selectedNotification
  );

  const filterList = (
    <Stack direction="row-reverse" gap={4} mt={2} pb={4} width="100%">
      <FormProvider {...methods}>
        <DateFilter
          fieldNamePrefix={TABLE_SEARCH_PARAMS.SENT_AT}
          chipLabel="Sent date"
        />
        <TableMultiSelectFilter
          fieldName={TABLE_SEARCH_PARAMS.TYPE}
          label="Notification Type"
          options={CORPORATE_ACTION_NOTIFICATION_OPTIONS}
          queryParameter={TABLE_SEARCH_PARAMS.TYPE}
        />
        <TableMultiSelectFilter
          fieldName={TABLE_SEARCH_PARAMS.STATUS}
          label="Status"
          options={NOTIFICATION_STATUS_OPTIONS}
          queryParameter={TABLE_SEARCH_PARAMS.STATUS}
        />
      </FormProvider>
    </Stack>
  );

  const searchBarProps = {
    ...searchFns,
    name: "search",
    placeholder: "Search by ticker, issuer",
  };

  if (
    data?.count === 0 &&
    !type &&
    !status &&
    !search &&
    !at &&
    !before &&
    !after
  ) {
    return (
      <EmptyState
        title="This user has not been sent any voluntary corporate action notifications."
        subtitle="When this user is sent notifications, they will appear here."
      />
    );
  }

  return (
    <Table
      testid="customer-corporate-action-notifications-table"
      count={data?.count || 0}
      columnData={columnData}
      columns={columns}
      isFetching={isFetching}
      isLoading={isLoading}
      noResultsData={{
        title: "No notifications found",
        subtitle:
          "Sorry, there are no notifications that match your applied filters.",
      }}
      paginateProps={paginateProps}
      rows={rowData}
      searchBarProps={searchBarProps}
      tableMinWidth={1100}
      title={`${data?.count} Corporate action notifications`}
      titleVariant="subtitle2"
      toolbarInside
      toolbarCollapse={{
        Icon: FilterList,
        collapseContent: filterList,
      }}
    />
  );
}
